import { useCallback, useState } from 'react';

import { useMarketplaceCart } from '@jane/bloom/hooks/cart/useMarketplaceCart';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { mergeCartItem } from '@jane/bloom/utils/cart/mergeCartItem';
import { formatWeight } from '@jane/bloom/utils/strings/formatWeight';

type CartItemToAdd = {
  count: number;
  priceId?: string;
  productId: number;
};

export function useBagTransfer() {
  const { cart, shoppingPreferences } = useRootLoaderData();
  const { createCart, deleteCart, newCart } = useMarketplaceCart();
  const [showWarningModal, setShowWarningModal] = useState(false);

  /**
   * Checks if there's a store mismatch and handles creating the pending cart.
   * @param itemToAdd - Optional new item to add to the cart when transferring
   * @returns true if transfer is needed (modal will be shown), false otherwise
   */
  const checkAndHandleTransfer = useCallback(
    (itemToAdd?: CartItemToAdd): boolean => {
      if (
        !shoppingPreferences ||
        !cart.storeId ||
        cart.storeId === shoppingPreferences?.storeId
      ) {
        return false;
      }

      const products = itemToAdd
        ? mergeCartItem(
            {
              count: itemToAdd.count,
              price_id: itemToAdd.priceId
                ? formatWeight(itemToAdd.priceId)
                : 'each',
              product_id: itemToAdd.productId,
            },
            cart.items
          )
        : cart.items.map((item) => ({
            count: item.count,
            price_id: item.priceId,
            product_id: Number(item.productId),
          }));

      createCart({
        products,
        reservationMode: shoppingPreferences.reservationMode,
        storeId: shoppingPreferences.storeId,
      });

      setShowWarningModal(true);
      return true;
    },
    [cart.items, cart.storeId, createCart, shoppingPreferences]
  );

  /**
   * Confirms the bag transfer and executes the success callback
   * @param onSuccess - Callback to execute after confirming (e.g., navigation or form submission)
   */
  const confirmTransfer = useCallback((onSuccess?: () => void) => {
    setShowWarningModal(false);

    if (onSuccess) {
      onSuccess();
    }
  }, []);

  /**
   * Cancels the bag transfer and deletes the pending cart
   */
  const cancelTransfer = useCallback(
    (onSuccess?: () => void) => {
      setShowWarningModal(false);

      if (newCart?.cart?.uuid) {
        deleteCart({ cartUuid: newCart.cart.uuid });
      }
      if (onSuccess) {
        onSuccess();
      }
    },
    [deleteCart, newCart]
  );

  return {
    cancelTransfer,
    checkAndHandleTransfer,
    confirmTransfer,
    newCart,
    showWarningModal,
  };
}
